import { useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { mdiAlert } from "@mdi/js";
import Icon from "@mdi/react";
import { Alert, Button, Flex, Input, Modal, notification } from "antd";

import { api } from "../../api";
import { userStore } from "../../stores/userStore";
import { usePendingAction } from "../../utils/usePendingAction";

import type { ConfirmEmailErrorCode } from "../../api/auth";

const RESEND_TIMEOUT = 60;

function formatTime(seconds: number) {
  const m = String(Math.floor(seconds / 60)).padStart(2, "0");
  const s = String(seconds % 60).padStart(2, "0");
  return `${m}:${s}`;
}

type Step = "send" | "verify";

interface EmailConfirmationModalProps {
  initialStep?: Step;
}

export function EmailConfirmationModal({
  initialStep = "send",
}: EmailConfirmationModalProps) {
  const { t } = useTranslation();
  const [step, setStep] = useState<Step>(initialStep);
  const [token, setToken] = useState("");
  const [code, setCode] = useState("");
  const [codeRequired, setCodeRequired] = useState(false);
  const [verifyErrorCode, setVerifyErrorCode] =
    useState<ConfirmEmailErrorCode | null>(null);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [isResent, setIsResent] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const { pending: sendLoading, run: runSend } = usePendingAction();
  const { pending: verifyLoading, run: runVerify } = usePendingAction();
  const { pending: resendLoading, run: runResend } = usePendingAction();

  const email = userStore.profile?.email ?? "";

  const FATAL_CODES: ConfirmEmailErrorCode[] = [
    "confirmation_expired",
    "token_not_found",
    "wrong_token",
    "max_confirmation_attempts_exceeded",
  ];

  function startTimer() {
    setSecondsLeft(RESEND_TIMEOUT);
    clearInterval(intervalRef.current ?? undefined);
    intervalRef.current = setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev <= 1) {
          clearInterval(intervalRef.current ?? undefined);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }

  async function handleSend() {
    await runSend(async () => {
      try {
        const result = await api.profile.sendProfileVerificationEmail();
        if (result.ok) {
          setToken(result.token);
          setCode("");
          setVerifyErrorCode(null);
          setCodeRequired(false);
          startTimer();
          setStep("verify");
        } else {
          notification.error({ message: t("errors.shared.common_error") });
        }
      } catch {
        notification.error({ message: t("errors.shared.common_error") });
      }
    });
  }

  async function handleVerify() {
    if (!code.trim()) {
      setCodeRequired(true);
      return;
    }
    await runVerify(async () => {
      setVerifyErrorCode(null);
      try {
        const result = await api.auth.confirmEmail(code, token);
        if (result.ok) {
          const profile = await api.profile.getProfile();
          userStore.setProfile(profile);
        } else {
          const errorCode = result.code ?? "token_not_found";
          setVerifyErrorCode(errorCode);
          if (FATAL_CODES.includes(errorCode)) {
            setSecondsLeft(0);
            clearInterval(intervalRef.current ?? undefined);
          }
        }
      } catch {
        notification.error({ message: t("errors.shared.common_error") });
      }
    });
  }

  async function handleResend() {
    await runResend(async () => {
      setCode("");
      setVerifyErrorCode(null);
      try {
        const result = await api.profile.sendProfileVerificationEmail();
        if (result.ok) {
          setToken(result.token);
          startTimer();
          setIsResent(true);
          setTimeout(() => setIsResent(false), 3000);
        } else {
          notification.error({ message: t("errors.shared.common_error") });
        }
      } catch {
        notification.error({ message: t("errors.shared.common_error") });
      }
    });
  }

  return (
    <Modal
      open
      footer={null}
      closable={false}
      centered
      width={440}
    >
      <h2 style={{ marginTop: 0 }}>{t("profile.emailConfirmModal.title")}</h2>

      {step === "send" && (
        <>
          <p>
            <Trans
              i18nKey="profile.emailConfirmModal.description"
              values={{ email }}
              components={{ strong: <strong /> }}
            />
          </p>
          <Button
            type="primary"
            size="large"
            block
            loading={sendLoading}
            onClick={handleSend}
          >
            {t("profile.emailConfirmModal.sendCode")}
          </Button>
        </>
      )}

      {step === "verify" && (
        <>
          <p>
            <Trans
              i18nKey="profile.emailConfirmModal.description"
              values={{ email }}
              components={{ strong: <strong /> }}
            />
          </p>

          <Input
            placeholder={t("profile.emailConfirmModal.codePlaceholder")}
            size="large"
            value={code}
            status={codeRequired ? "error" : ""}
            onChange={(e) => {
              setCode(e.target.value);
              if (verifyErrorCode) setVerifyErrorCode(null);
              if (codeRequired) setCodeRequired(false);
            }}
            onPressEnter={handleVerify}
            autoComplete="one-time-code"
            inputMode="numeric"
            style={{ marginBottom: 4 }}
          />
          {codeRequired && (
            <div style={{ color: "#ff4d4f", fontSize: 12, marginBottom: 8 }}>
              {t("signup.details.required")}
            </div>
          )}

          {verifyErrorCode && (
            <Alert
              type="error"
              showIcon
              icon={<Icon path={mdiAlert} size={0.75} color="#ff4d4f" />}
              style={{ marginTop: 8 }}
              description={t(
                `profile.emailConfirmModal.errors.${verifyErrorCode}`,
              )}
            />
          )}

          <Button
            type="primary"
            size="large"
            block
            loading={verifyLoading}
            disabled={resendLoading}
            style={{ marginTop: 16 }}
            onClick={handleVerify}
          >
            {t("profile.emailConfirmModal.verify")}
          </Button>

          <Flex justify="space-between" align="center" style={{ marginTop: 12 }}>
            <span style={{ color: "#8c8c8c", fontSize: 13 }}>
              {t("profile.emailConfirmModal.resendIn")} {formatTime(secondsLeft)}
            </span>
            {isResent ? (
              <Button size="small" disabled>
                {t("profile.emailConfirmModal.codeResent")}
              </Button>
            ) : (
              <Button
                size="small"
                disabled={secondsLeft > 0 || verifyLoading}
                loading={resendLoading}
                onClick={handleResend}
              >
                {t("profile.emailConfirmModal.resend")}
              </Button>
            )}
          </Flex>
        </>
      )}
    </Modal>
  );
}
